/* eslint-disable camelcase */
import { Avatar, Button, Card, CardContent, Divider, Grid, LinearProgress, Stack, Typography } from '@mui/material'
import AgriAction from 'agriAdministration/actions/AgriAction'
import { push } from '@lagunovsky/redux-react-router'
import InstallationAction from 'installation/actions/InstallationAction'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import { mainBlue } from 'utils/constants/ColorTheme'
import { getDate } from 'utils/DateUtil'
import { getLogin, getSetting, getSettingInt } from 'utils/SettingUtils'
import { getSandreLabel } from 'utils/StringUtil'
import DeclarationContactsStep from './steps/DeclarationContactsStep'
import DeclarationPointsStep from './steps/DeclarationPointsStep'
import DeclarationRecapStep from './steps/DeclarationRecapStep'
import DeclarationSamplerStep from './steps/DeclarationSamplerStep'
import ProgressCard from 'components/card/ProgressCard'
import { useParams } from 'react-router'
import useTitle from 'utils/customHook/useTitle'
import { CardTitle } from 'components/card/NewCard'
import ProgressBar from 'components/progress/ProgressBar'
import { Check, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import propTypes from 'prop-types'
import ModalAddPoint from './modals/ModalAddPoint'
import { compact, orderBy, groupBy, uniqBy, keys, includes } from 'lodash'
import SelectContactModal from './modals/SelectContactModal'
import { DECLARATION_STATUS, SITU_POINT_PUMP, SITU_PUMP_COUNTER, SURVEY_TYPES } from 'agriAdministration/constants/AgriConstants'
import moment from 'moment'
import { hasValue } from 'utils/NumberUtil'
import ModalWarnNoConso from './modals/ModalWarnNoConso'
import ModalSynthese from './modals/ModalSynthese'

const ADVANCEMENT_STEP = 0
const SAMPLER_STEP = 1
const CONTACTS_STEP = 2
const POINTS_STEP = 3
const RECAP_STEP = 4

const isFinalized = (statusCode) => statusCode >= DECLARATION_STATUS.FINALIZED_BY_AGRI

const LinearProgressWithStyle = ({ value }) => (
    <LinearProgress
        variant='determinate'
        value={value}
        sx={{
            height: 25,
            '& .MuiLinearProgress-bar': {
                backgroundColor: mainBlue,
            },
        }}
    />
)

LinearProgressWithStyle.propTypes = {
    value: propTypes.number.isRequired,
}

const styleButton = { width: '100%' }

const DeclarationStepperApp = ({}) => {
    const [openModalPoint, setOpenModalPoint] = useState(false)
    const [openModalContact, setOpenModalContact] = useState(false)
    const [openModalNoConso, setOpenModalNoConso] = useState(false)
    const [openModalSynthese, setOpenModalSynthese] = useState(false)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [instsWithoutIndexState, setInstsWithoutIndexState] = useState([])

    const {
        declaration,
        survey,
        exploitation,
        contributors,
        sandreCodes,
        accountUser,
        installationsWithGeo,
        citiesIndex,
        contacts,
        variousMateriels,
    } = useSelector(store => ({
        declaration: store.AgriReducer.declaration,
        survey: store.AgriReducer.survey,
        exploitation: store.AgriReducer.exploitation,
        contributors: store.ContributorReducer.contributors,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        accountUser: store.AccountReducer.accountUser,
        installationsWithGeo: store.InstallationReducer.installationsWithGeo,
        citiesIndex: store.CityReducer.citiesIndex,
        contacts: store.ContactReducer.contacts,
        variousMateriels: store.VariousMaterielReducer.variousMateriels,
    }), shallowEqual)

    const [comment, setComment] = useState(declaration.comment)

    const dispatch = useDispatch()
    const params = useParams()

    const step = parseInt(params.step)

    const points = useMemo(() => {
        const exploitPoints = declaration.link_exploitationInstallation.map(linkPoint => {
            const point = installationsWithGeo.find(i => i.id === linkPoint.idInstallation)
            if (point) {
                const city = citiesIndex[point.townCode] || {}
                return {
                    ...point,
                    cityName: city.name,
                }
            }
            return undefined
        })
        return orderBy(compact(exploitPoints), ['townCode', 'cityName', 'parcel', 'section', 'code'])
    }, [declaration, installationsWithGeo, citiesIndex])

    const allContributors = useMemo(() => {
        const contactsExploitation = compact(exploitation.link_contributors.map((c) => {
            const linkUpdated = declaration.link_contributorTypes.find((cT) => !cT.isTemp && cT.idContributor === c.contactCode && cT.contributorType === c.contributorType)
            if (!linkUpdated) {
                return {
                    idContributor: c.contactCode,
                    isTemp: false,
                    contributorType: c.contributorType,
                    surveyId: declaration.idSurvey,
                    idExploitation: exploitation.idExploitation,
                    updateLogin: getLogin(),
                    mode: 'u',
                }
            }
            return null
        }))
        const contactsExploitationAndDecla = [...contactsExploitation, ...declaration.link_contributorTypes.filter((cT) => cT.mode !== 'd')]

        const preleveur = contributors.find((c) => c.id === exploitation.operatorCode) || {}
        const legalRepresentative = contacts.find(c => c.id === preleveur.legalRepresentative) || {}
        const contactsWithRep = !contactsExploitationAndDecla.find((cT) => cT.contributorType === -1 && cT.mode !== 'd')
            && preleveur.structureType !== 1
            && legalRepresentative ?
            [
                ...contactsExploitationAndDecla,
                {
                    idContributor: legalRepresentative.id,
                    isTemp: false,
                    contributorType: -1,
                    surveyId: declaration.idSurvey,
                    idExploitation: exploitation.idExploitation,
                    updateLogin: getLogin(),
                    mode: 'u',
                },
            ]
            :
            contactsExploitationAndDecla

        return orderBy(contactsWithRep, 'contributorType')
    }, [declaration, exploitation, contacts, contributors])

    const progress = useMemo(() => {
        const newProgress = [0, 0, 0, 0]
        const { lastStep, statusCode } = declaration
        if (lastStep > SAMPLER_STEP) {
            newProgress[SAMPLER_STEP - 1] = 100
        } else if (lastStep === SAMPLER_STEP) {
            newProgress[SAMPLER_STEP - 1] = 10
        } else {
            newProgress[SAMPLER_STEP - 1] = 0
        }
        if (lastStep > CONTACTS_STEP) {
            newProgress[CONTACTS_STEP - 1] = 100
        } else if (lastStep === CONTACTS_STEP) {
            newProgress[CONTACTS_STEP - 1] = 10
        } else {
            newProgress[CONTACTS_STEP - 1] = 0
        }
        if (lastStep >= POINTS_STEP) {
            if (declaration.link_declarationInstallation?.length > 0) {
                const length = declaration.link_declarationInstallation.length
                let pointsHavingUsages = 0
                declaration.link_declarationInstallation.map((p) => {
                    if (p.link_usages?.length > 0) {
                        pointsHavingUsages = pointsHavingUsages + 1
                    }
                })
                const pourcentage = (pointsHavingUsages / length) * 90 + 10
                newProgress[POINTS_STEP - 1] = pourcentage
            } else {
                newProgress[POINTS_STEP - 1] = 0
            }
        } else {
            newProgress[POINTS_STEP - 1] = 0
        }
        if (lastStep === RECAP_STEP && statusCode >= DECLARATION_STATUS.FINALIZED_BY_AGRI) {
            newProgress[RECAP_STEP - 1] = 100
        } else if (lastStep === RECAP_STEP) {
            newProgress[RECAP_STEP - 1] = 75
        } else {
            newProgress[RECAP_STEP - 1] = 0
        }
        return newProgress
    }, [declaration])

    const readMode = useMemo(() => declaration?.statusCode >= DECLARATION_STATUS.VALIDATED || accountUser?.consultant === '1', [declaration])

    useEffect(() => {
        dispatch(AgriAction.fetchExploitationUser()).then(() => {
            dispatch(InstallationAction.fetchInstallationsByExploitationId(exploitation.idExploitation)).then(() => {
                dispatch(AgriAction.fetchDeclaration(params.idDeclaration)).then((dec) => {
                    setDataLoaded(true)
                    setComment(dec.comment)
                })
            })
        })
    }, [])

    useTitle(() => ([{
        title: i18n.planning,
        href: 'planning',
    }, {
        title: i18n.surveys,
        href: 'planning/surveys',
    }, {
        title: `${survey.name || ''} [${survey.year || ''}]`,
        href: `survey/${survey.idSurvey}`,
    }, {
        title: `${i18n.declaration}${exploitation.codification ? ` ${exploitation.codification}` : ''}`,
        href: `survey/${survey.idSurvey}/declaration/${params.idDeclaration}/stepper/0`,
    }]), [survey, exploitation])

    const onChangeDeclaration = (key, value) => {
        const updateDeclaration = {
            ...declaration,
            [key]: value,
            statusCode: declaration.statusCode >= DECLARATION_STATUS.FINALIZED_BY_AGRI ? declaration.statusCode : DECLARATION_STATUS.STARTED,
        }
        if (key !== 'comment') {
            setDataLoaded(false)
            dispatch(AgriAction.updateDeclaration(updateDeclaration)).then(() => setDataLoaded(true))
        }
    }

    const addPoint = (newPoints) => onChangeDeclaration('link_declarationInstallation', newPoints)

    const getPrevStep = (actualStep) => {
        const { link_params = [] } = survey
        if ((getSetting(link_params, `step${actualStep - 1}`) || '1') === '0') {
            return getPrevStep(actualStep - 1)
        }
        return actualStep - 1
    }

    const getNextStep = (actualStep) => {
        const { link_params = [] } = survey
        if ((getSetting(link_params, `step${actualStep + 1}`) || '1') === '0') {
            return getNextStep(actualStep + 1)
        }
        return actualStep + 1
    }

    const startDeclaration = () => {
        dispatch(push(`/survey/${survey.idSurvey}/declaration/${params.idDeclaration}/stepper/${getNextStep(0)}`))
    }

    const getMateriels = (idInstallation) => {
        const link_equipments = declaration.link_declarationInstallation.find((p) => p.idInstallation === idInstallation).link_equipments || []
        const newMats = link_equipments.filter((mLink) => !mLink.idMatAttachment && mLink.mode !== 'd')
        const deletedMats = link_equipments.filter((mLink) => !mLink.idMatAttachment && mLink.mode === 'd')
        const deletedCounters = link_equipments.filter((mLink) => mLink.idMatAttachment && mLink.mode === 'd')
        const exploitationPumps = declaration.link_exploitationMateriel.filter((m) => m.siteType === SITU_POINT_PUMP &&
            m.siteCode === idInstallation &&
            !deletedMats.find((mLink) => m.idVarious === mLink.idMat),
        ) || []
        const mergedPumpsIds = uniqBy([
            ...exploitationPumps.map((m) => m.idVarious),
            ...newMats.map((m) => m.idMat),
        ], obj => obj)
        const exploitationCountersId = compact(mergedPumpsIds.flatMap((pumpId) => {
            const pumpSitu = declaration.link_exploitationMateriel.filter((situ) => situ.siteType === SITU_PUMP_COUNTER &&
                situ.siteCode === pumpId &&
                !deletedCounters.find((mLink) => pumpId === mLink.idMat),
            )
            if (pumpSitu) {
                return pumpSitu.map((s) => s.idVarious)
            }
            return null
        }))
        const newCounters = link_equipments.filter((mLink) => mLink.idMatAttachment && mLink.mode !== 'd' && mergedPumpsIds.includes(mLink.idMat))
        const mergedCountersIds = uniqBy([
            ...exploitationCountersId,
            ...newCounters.map((m) => m.idMatAttachment),
        ], obj => obj)
        const materiels = variousMateriels.filter((m) => [...mergedPumpsIds, ...mergedCountersIds].includes(m.id) &&
            (!hasValue(m.administrator) || (m.administrator === exploitation.operatorCode) || m.isShared),
        )
        return materiels
    }

    const validateDeclaration = () => {
        if (readMode) {
            dispatch(push(`/survey/${survey.idSurvey}`))
        } else {
            const instsToCheck = declaration.link_declarationInstallation.filter((instal) => [1, 3].includes(instal.stateCode))
            const instsWithoutIndex = compact(instsToCheck.map((i) => {
                if (!i.link_usagesCurrent?.length) {
                    return i.idInstallation
                }
                const materiels = getMateriels(i.idInstallation)
                const matsIds = materiels.map((m) => m.id)
                const chroniclesByMat = groupBy(declaration.link_chronicles.filter((c) => matsIds.includes(c.idMat)), 'idMat')
                const indexsFoundForYear = compact(keys(chroniclesByMat).map((matId) => {
                    const lastIndex = orderBy(chroniclesByMat[matId], 'measureDate', 'desc')[0]
                    if (lastIndex && moment(lastIndex.measureDate).year() >= (survey.surveyType === SURVEY_TYPES.INTERMEDIATE ? survey.year : survey.year - 1)) {
                        return true
                    }
                    return undefined
                }))
                if (indexsFoundForYear.length === 0) {
                    return i.idInstallation
                }
                return undefined
            }))
            if (instsWithoutIndex.length && !instsWithoutIndexState.length && getSettingInt(survey.link_params, 'consos') === 1 && getSettingInt(survey.link_params, 'step3') === 1) {
                setOpenModalNoConso(true)
                setInstsWithoutIndexState(instsWithoutIndex)
            } else {
                dispatch(AgriAction.updateDeclaration({ ...declaration, statusCode: DECLARATION_STATUS.VALIDATED_BY_ADMIN, comment }))
                setInstsWithoutIndexState([])
                setOpenModalSynthese(true)
            }
        }
    }

    const previousStep = () => {
        dispatch(push(`/survey/${survey.idSurvey}/declaration/${params.idDeclaration}/stepper/${getPrevStep(step)}`))
        if (!readMode && !isFinalized(declaration.statusCode)) {
            const lastStep = declaration.lastStep ?? ADVANCEMENT_STEP
            const updateDeclaration = {
                ...declaration,
                lastStep: lastStep >= step ? lastStep : getPrevStep(step),
                statusCode: lastStep === SAMPLER_STEP && declaration.statusCode === DECLARATION_STATUS.STARTED ?
                    DECLARATION_STATUS.NOT_STARTED[1]
                    :
                    declaration.statusCode,
            }
            dispatch(AgriAction.updateDeclaration(updateDeclaration))
        }
    }

    const nextStep = () => {
        const preleveur = contributors.find((c) => c.id === exploitation.operatorCode) || {}
        if (contributors?.length) {
            if (!readMode && step === CONTACTS_STEP &&
                preleveur.structureType !== 1 && !allContributors.find((cT) => cT.contributorType === -1 && cT.idExploitation === exploitation.idExploitation && cT.mode !== 'd')) {
                dispatch(ToastrAction.error(i18n.fillLegalRepresentative))
            } else {
                dispatch(push(`/survey/${survey.idSurvey}/declaration/${params.idDeclaration}/stepper/${getNextStep(step)}`))
                if (!readMode && !isFinalized(declaration.statusCode)) {
                    const lastStep = declaration.lastStep ?? ADVANCEMENT_STEP
                    const updateDeclaration = {
                        ...declaration,
                        lastStep: lastStep > step ? lastStep : getNextStep(step),
                        statusCode: lastStep === ADVANCEMENT_STEP && includes(DECLARATION_STATUS.NOT_STARTED, declaration.statusCode) ?
                            DECLARATION_STATUS.STARTED
                            :
                            declaration.statusCode,
                    }
                    dispatch(AgriAction.updateDeclaration(updateDeclaration))
                }
            }
        }
    }

    const getStep = () => {
        const props = {
            readMode,
            onChangeDeclaration,
        }
        switch (step) {
            case ADVANCEMENT_STEP:
                if (!dataLoaded) {
                    return <ProgressBar withMessage indeterminate />
                }
                return (
                    <Stack spacing={1}>
                        <Card>
                            <CardTitle title={i18n.description} />
                            <CardContent>
                                <Stack direction={'row'} spacing={2} divider={<Divider orientation='vertical' flexItem />} >
                                    <Grid container>
                                        <Typography sx={{ fontWeight: 'bold' }}>{i18n.year}</Typography> : {survey.year}
                                    </Grid>
                                    <Grid container>
                                        <Typography sx={{ fontWeight: 'bold' }}>{i18n.status}</Typography> : {getSandreLabel(sandreCodes, SANDRE.CMS_STATUT, survey.statusCode)}
                                    </Grid>
                                    <Grid container>
                                        <Typography sx={{ fontWeight: 'bold' }}>{i18n.startDate}</Typography> : {survey.startDate ? getDate(survey.startDate) : ''}
                                    </Grid>
                                    <Grid container>
                                        <Typography sx={{ fontWeight: 'bold' }}>{i18n.endDate}</Typography> : {survey.endDate ? getDate(survey.endDate) : ''}
                                    </Grid>
                                    <Grid container>
                                        <Typography sx={{ fontWeight: 'bold' }}>{i18n.adminEndDate}</Typography> : {survey.adminEndDate ? getDate(survey.adminEndDate) : ''}
                                    </Grid>
                                </Stack>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardTitle title={i18n.advancementDeclaration} />
                            <CardContent sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Stack
                                    width={500}
                                    spacing={3}
                                >
                                    {(getSetting(survey.link_params, 'step1') || '1') !== '0' && (
                                        <Grid container spacing={3} alignItems={'center'}>
                                            <Grid size={6}>
                                                <LinearProgressWithStyle
                                                    value={progress[SAMPLER_STEP - 1] || 0}
                                                />
                                            </Grid>
                                            <Grid size={6}>{i18n.verifyCoordinates}</Grid>
                                        </Grid>
                                    )}
                                    {(getSetting(survey.link_params, 'step2') || '1') !== '0' && (
                                        <Grid container spacing={3} alignItems={'center'}>
                                            <Grid size={6}>
                                                <LinearProgressWithStyle
                                                    value={progress[CONTACTS_STEP - 1] || 0}
                                                />
                                            </Grid>
                                            <Grid size={6}>{i18n.verifyExploitation}</Grid>
                                        </Grid>
                                    )}
                                    {(getSetting(survey.link_params, 'step3') || '1') !== '0' && (
                                        <Grid container spacing={3} alignItems={'center'}>
                                            <Grid size={6}>
                                                <LinearProgressWithStyle
                                                    value={progress[POINTS_STEP - 1] || 0}
                                                />
                                            </Grid>
                                            <Grid size={6}>{i18n.declareUses}</Grid>
                                        </Grid>
                                    )}
                                    <Grid container spacing={3} alignItems={'center'}>
                                        <Grid size={6}>
                                            <LinearProgressWithStyle
                                                variant='determinate'
                                                value={progress[RECAP_STEP - 1] || 0}
                                            />
                                        </Grid>
                                        <Grid size={6}>{i18n.commentAndValidation}</Grid>
                                    </Grid>
                                </Stack>
                            </CardContent>
                        </Card>
                        <Typography textAlign={'center'}>{i18n.declarationStepperDescription}</Typography>
                    </Stack>
                )
            case SAMPLER_STEP: default:
                return <DeclarationSamplerStep {...props} />
            case CONTACTS_STEP:
                return <DeclarationContactsStep allContributors={allContributors} {...props} />
            case POINTS_STEP:
                return <DeclarationPointsStep />
            case RECAP_STEP:
                return <DeclarationRecapStep {...props} comment={comment} setComment={setComment} />
        }
    }

    const getFormattedTitle = (title) => (
        <Grid container spacing={1} alignItems={'center'}>
            <Avatar sx={{ width: '36px', height: '36px', color: 'white', backgroundColor: `${mainBlue} !important` }}>{step}/4</Avatar>
            <Typography sx={{ fontWeight: 'bold', fontSize: '18.2px' }}>{title}</Typography>
        </Grid>
    )

    const getStepLibelle = () => {
        switch (step) {
            case SAMPLER_STEP: case CONTACTS_STEP: default:
                return getFormattedTitle(i18n.iCheckMyInformations)
            case POINTS_STEP:
                return getFormattedTitle(i18n.iSelectMyPoints)
            case RECAP_STEP:
                return getFormattedTitle(i18n.iCheckMyDeclaration)
        }
    }

    const getStepAction = () => {
        switch (step) {
            case SAMPLER_STEP: case CONTACTS_STEP: case POINTS_STEP:
                return (
                    <>
                        <Grid size={2}>
                            <Button variant='contained' color='primary' onClick={previousStep} sx={styleButton}>
                                <KeyboardArrowLeft />
                            </Button>
                        </Grid>
                        <Grid size={2} offset={2}>
                            <Button variant='contained' color='primary' onClick={nextStep} sx={styleButton}>
                                <KeyboardArrowRight />
                            </Button>
                        </Grid>
                    </>
                )
            case RECAP_STEP:
                return (
                    <>
                        <Grid size={2}>
                            <Button variant='contained' color='primary' onClick={previousStep} sx={styleButton}>
                                <KeyboardArrowLeft />
                            </Button>
                        </Grid>
                        <Grid size={2} offset={2}>
                            <Button
                                variant='contained'
                                color='primary'
                                onClick={validateDeclaration}
                                sx={styleButton}
                                endIcon={<Check />}
                            >
                                {i18n.validate}
                            </Button>
                        </Grid>
                    </>
                )
            case ADVANCEMENT_STEP: default:
                return (
                    <>
                        <Grid size={2}>
                            <Button variant='outlined' color='primary' onClick={() => dispatch(push(`/survey/${survey.idSurvey}`))} sx={styleButton}>
                                {i18n.returnToSurvey}
                            </Button>
                        </Grid>
                        <Grid size={2}>
                            <Button variant='outlined' color='primary' onClick={() => dispatch(push(`/dossiers/${declaration.idExploitation}/dashboard`))} sx={styleButton}>
                                {i18n.returnToFolder}
                            </Button>
                        </Grid>
                        <Grid size={2}>
                            <Button variant='contained' color='primary' onClick={startDeclaration} sx={styleButton}>
                                {i18n.next}
                            </Button>
                        </Grid>
                    </>
                )
        }
    }

    const getActions = () => {
        switch (step) {
            case CONTACTS_STEP:
                return ([
                    {
                        icon: 'note_add',
                        onClick: () => setOpenModalContact(true),
                        displayed: !readMode,
                        tooltip: i18n.addContributor,
                    },
                ])
            case POINTS_STEP:
                return ([
                    {
                        icon: 'note_add',
                        onClick: () => setOpenModalPoint(true),
                        displayed: !readMode,
                        tooltip: i18n.addNewSamplingPoint,
                    },
                ])
            default: return []
        }
    }

    const showLegalRepresentative = () => {
        return !allContributors.find((c) => c.mode !== 'd' && c.contributorType === -1)
    }

    const onSaveContact = (contributor, type) => {
        const id = contributor.idContributorTemp || contributor.idContributor || contributor.id
        const newLink = {
            contributorType: type,
            surveyId: declaration.idSurvey,
            idExploitation: exploitation.idExploitation,
            updateLogin: getLogin(),
        }
        const contribFormatted = {
            ...contributor,
            road: contributor.address,
            addressComplement: contributor.additionalAddress,
            fax: contributor.desktopTel,
            postalCode: contributor.postalBox,
            idContributor: id,
            surveyId: declaration.idSurvey,
            contactType: 2,
        }
        // si le contact est déjà lié
        if (id && allContributors.find((cT) => cT.idContributor === id && !!cT.isTemp === !!contributor.idContributorTemp && cT.contributorType === type)) {
            dispatch(ToastrAction.warning(i18n.linkAlreadyExists))
        // si c'est une création de nouveau contact
        } else if (!id) {
            dispatch(AgriAction.updateDeclarationContributor(declaration.idSurvey, contribFormatted)).then((idReturned) => {
                onChangeDeclaration('link_contributorTypes', [
                    ...declaration.link_contributorTypes,
                    {
                        ...newLink,
                        idContributor: idReturned,
                        isTemp: true,
                        mode: 'c',
                    },
                ])
            })
            setOpenModalContact(false)
        // si c'est un rajout de lien non existant
        } else {
            dispatch(AgriAction.updateDeclarationContributor(declaration.idSurvey, contribFormatted)).then(() => {
                onChangeDeclaration('link_contributorTypes', [
                    ...declaration.link_contributorTypes,
                    {
                        ...newLink,
                        idContributor: id,
                        isTemp: !!contributor.idContributorTemp,
                        mode: 'c',
                    },
                ])
            })
            setOpenModalContact(false)
        }
    }

    return (
        <Grid size={12} padding={1}>
            <Card>
                <CardTitle
                    title={step !== ADVANCEMENT_STEP ? getStepLibelle() : `${i18n.survey} : ${survey.name || ''}`}
                    actions={step !== ADVANCEMENT_STEP ? getActions() : []}
                />
                <CardContent>
                    <Stack spacing={3}>
                        {dataLoaded ? getStep() : (
                            <Grid size={12}>
                                <ProgressCard indeterminate withMessage round />
                            </Grid>
                        )}
                        {dataLoaded && (
                            <Grid
                                container
                                size={12}
                                justifyContent='space-around'
                                alignItems='center'
                            >
                                {getStepAction()}
                            </Grid>
                        )}
                    </Stack>
                </CardContent>
            </Card>
            {openModalPoint && (
                <ModalAddPoint
                    open={openModalPoint}
                    toggleModal={() => setOpenModalPoint(!openModalPoint)}
                    addPoint={addPoint}
                    selectedPoints={declaration.link_declarationInstallation}
                    installationPoints={points}
                    declaration={declaration}
                    exploitation={exploitation}
                />
            )}
            {openModalContact && (
                <SelectContactModal
                    showLegalRepresentative={showLegalRepresentative}
                    contributor={{}}
                    open={openModalContact}
                    onSave={onSaveContact}
                    onDelete={() => setOpenModalContact(false)}
                    onCancel={() => setOpenModalContact(false)}
                />
            )}
            {openModalNoConso && (
                <ModalWarnNoConso
                    open={openModalNoConso}
                    toggleModal={() => setOpenModalNoConso(false)}
                    onValidateDeclaration={validateDeclaration}
                    instsWithoutIndex={instsWithoutIndexState}
                />
            )}
            {openModalSynthese && (
                <ModalSynthese
                    open={openModalSynthese}
                    toggleModal={() => setOpenModalSynthese(false)}
                    push={() => dispatch(push(`/survey/${survey.idSurvey}`))}
                />
            )}
        </Grid>
    )
}

export default DeclarationStepperApp
